import { describeAnswer } from './answers';
import type { CompatibilityResult, Question, VisitorAnswer } from './types';

export { describeAnswer };

/**
 * Contact submission: shape, validation and the exact payload that leaves the
 * browser.
 *
 * Two rules govern this file:
 *
 *  1. Collect the minimum. First name, ONE contact method, an optional
 *     message, and the result category *if there is one*. That is all that is
 *     needed to reply to someone, so that is all that is asked for.
 *
 *  2. Nothing implicit. `buildSubmission` is the only function that produces
 *     an outbound payload, so there is exactly one place to audit when asking
 *     "what does this app actually send?". Answers are attached only when
 *     `shareAnswers` is explicitly true.
 *
 * The same rules are enforced independently on the server (see
 * `backend/src/AreWeCompatible.Api/Validation`). Client validation is for
 * comfort; server validation is for correctness.
 */

export type ContactMethod = 'email' | 'phone';

/**
 * There is deliberately no general `consent` field.
 *
 * There used to be one: a required "Send this to James" tick sitting directly
 * above a button labelled "Send to James". It was never part of the payload —
 * `buildSubmission` below has always been the whole answer to "what is sent?"
 * and it never referred to it — so it asked for the same permission twice and
 * bought nothing. Pressing the button is the consent.
 *
 * `shareAnswers` is a different thing and stays: it decides whether her
 * personal answers travel at all, it defaults to off, and the server rejects a
 * payload carrying answers without it.
 */
export interface ContactFormValues {
  firstName: string;
  contactMethod: ContactMethod;
  contactValue: string;
  message: string;
  shareAnswers: boolean;
}

export const emptyContactForm: ContactFormValues = {
  firstName: '',
  contactMethod: 'email',
  contactValue: '',
  message: '',
  shareAnswers: false,
};

export const CONTACT_LIMITS = {
  firstNameMax: 60,
  contactValueMax: 120,
  messageMax: 1000,
} as const;

export const contactMethodMeta: Record<
  ContactMethod,
  { label: string; placeholder: string; inputType: string; autoComplete: string; hint: string }
> = {
  email: {
    label: 'Email',
    placeholder: 'you@example.com',
    inputType: 'email',
    autoComplete: 'email',
    hint: 'I will only ever use this to reply to you.',
  },
  phone: {
    label: 'Phone',
    placeholder: '021 123 4567',
    inputType: 'tel',
    autoComplete: 'tel',
    hint: 'A text first, not a call out of the blue.',
  },
};

export type ContactErrors = Partial<Record<keyof ContactFormValues, string>>;

// Deliberately permissive. An over-strict email regex rejects real addresses,
// and the only cost of letting a typo through is that James cannot reply.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Unicode format characters: no width, no glyph, and not matched by `\s`,
 * which covers separators rather than format characters.
 *
 * A zero-width space or byte order mark rides along invisibly when an address
 * is pasted or typed on a phone keyboard. It sailed through the regex above,
 * looked perfect in the field, and then the email provider refused the entire
 * send because the Reply-To was not ASCII — which, with no database behind
 * this, destroyed her message rather than degrading it.
 *
 * Stripped rather than rejected: she cannot see the character, so telling her
 * the address is wrong would be baffling, and what she meant is unambiguous
 * once the invisible part is gone. The server does the same thing, and that is
 * the copy that counts.
 */
const INVISIBLE_RE = /\p{Cf}/gu;

export function stripInvisible(value: string): string {
  return value.replace(INVISIBLE_RE, '');
}
// At least 7 digits, allowing spaces, dashes, brackets and a leading +.
const PHONE_RE = /^\+?[\d\s()-]{7,}$/;

export function validateContactForm(values: ContactFormValues): ContactErrors {
  const errors: ContactErrors = {};

  const firstName = values.firstName.trim();
  if (firstName.length === 0) {
    errors.firstName = 'A first name is enough. Whatever you like being called.';
  } else if (firstName.length > CONTACT_LIMITS.firstNameMax) {
    errors.firstName = `That is longer than ${CONTACT_LIMITS.firstNameMax} characters.`;
  }

  const contactValue = stripInvisible(values.contactValue).trim();
  if (contactValue.length === 0) {
    errors.contactValue = 'I need one way to reach you.';
  } else if (contactValue.length > CONTACT_LIMITS.contactValueMax) {
    errors.contactValue = 'That looks too long to be right.';
  } else if (values.contactMethod === 'email' && !EMAIL_RE.test(contactValue)) {
    errors.contactValue = 'That does not look like an email address.';
  } else if (values.contactMethod === 'phone' && !PHONE_RE.test(contactValue)) {
    errors.contactValue = 'That does not look like a phone number.';
  }

  if (values.message.length > CONTACT_LIMITS.messageMax) {
    errors.message = `Keep it under ${CONTACT_LIMITS.messageMax} characters.`;
  }

  return errors;
}

export function isValidContactForm(values: ContactFormValues): boolean {
  return Object.keys(validateContactForm(values)).length === 0;
}

/* ---------------------------------------------------------------------------
   The outbound payload
   --------------------------------------------------------------------------- */

export interface SharedAnswerPayload {
  questionId: string;
  /** The question text, so the payload is readable without the app. */
  question: string;
  /** The visitor's answer, already rendered to human-readable text. */
  answer: string;
}

export interface ContactSubmissionPayload {
  profileId: string;
  firstName: string;
  contactMethod: ContactMethod;
  contactValue: string;
  message: string | null;
  /**
   * Null when she came straight from the profile without doing the questions.
   *
   * It has to be nullable, and nothing may be invented to fill it. Sending
   * "worth-a-conversation" for somebody who never answered anything would put
   * a compatibility verdict in James's inbox that no human being produced, and
   * he would read it as though she had.
   */
  resultCategory: string | null;
  /** Mirrors the checkbox. The server rejects answers when this is false. */
  consentToShareAnswers: boolean;
  /** Present only when consentToShareAnswers is true. */
  answers: SharedAnswerPayload[] | null;
  /** Where she came from, e.g. "card". Never a user identifier. */
  source: string | null;
}

/**
 * Builds the exact object that is POSTed. Nothing else in the app constructs
 * an outbound body, so this function is the whole answer to "what is sent?".
 *
 * `result` is optional, and so are the questions and answers that go with it.
 * A submission with no result carries no answers either — there are none to
 * carry — which is the same rule the server enforces from its own side.
 */
export function buildSubmission(args: {
  profileId: string;
  values: ContactFormValues;
  result?: CompatibilityResult | null;
  questions?: Question[];
  answers?: Record<string, VisitorAnswer>;
  source: string | null;
}): ContactSubmissionPayload {
  const {
    profileId,
    values,
    result = null,
    questions = [],
    answers = {},
    source,
  } = args;
  const message = values.message.trim();

  // Nothing to share without a journey, whatever the tick says. The form hides
  // the control in that case; this is the guarantee rather than the courtesy.
  const shareAnswers = values.shareAnswers && result !== null;

  const sharedAnswers: SharedAnswerPayload[] = shareAnswers
    ? questions.flatMap((question) => {
        const answer = answers[question.id];
        if (!answer) return [];
        return [
          {
            questionId: question.id,
            question: question.prompt,
            answer: describeAnswer(question, answer),
          },
        ];
      })
    : [];

  return {
    profileId,
    firstName: values.firstName.trim(),
    contactMethod: values.contactMethod,
    // Stripped here too, not just in the validator: what was checked has to be
    // what is sent, or the check was of a different string than the one that
    // matters.
    contactValue: stripInvisible(values.contactValue).trim(),
    message: message.length > 0 ? message : null,
    resultCategory: result?.category ?? null,
    consentToShareAnswers: shareAnswers,
    answers: shareAnswers ? sharedAnswers : null,
    source,
  };
}
